import { useState, useEffect } from 'react';
import { useQuery, gql } from '@apollo/client';
import FilterPanel from '../components/FilterPanel';
import CandidateTable from '../components/CandidateTable';
import CandidateModal from '../components/CandidateModal';
import Pagination from '../components/Pagination';
import TurnstileWidget from '../components/TurnstileWidget';
import useAuthStore from '../stores/authStore';
import { isResultsLimitAllowed, getMaxResultsPerPage } from '../config/levelLimits';
import './CandidateSearch.css';

const GET_CANDIDATES = gql`
  query GetCandidates($filters: FilterInput, $limit: Int, $offset: Int) {
    candidates(filters: $filters, limit: $limit, offset: $offset) {
      slug
      name
      age
      isWoman
      isTechnical
      ycFounderLabel
      avatarUrl
      linkedin
      education
      employment
      location
      country
      region
      timing
      emailSettings
      videoLink
      calendlyLink
      intro
      lifeStory
      freeTime
      other
      impressiveThing
      interests
      responsibilities
      companyName
      companyUrl
      companyDescription
      companyProgress
      fundingStatus
      hasIdea
      ideas
      hasCf
      currentCfLinkedin
      currentCfTechnical
      whyLookingForThirdCf
      reqFreeText
      equity
      cfHasIdea
      cfHasIdeaImportance
      cfIsTechnical
      cfIsTechnicalImportance
      cfResponsibilities
      cfResponsibilitiesImportance
      cfLocation
      cfLocationImportance
      cfLocationKmRange
      cfAgeMin
      cfAgeMax
      cfAgeImportance
      cfTimingImportance
      cfInterestsImportance
      cfIsFellowAlumniImportance
      cfIsYcAlumImportance
      cfIsWomanImportance
      lastSeenAt
      savedAt
      level
    }
    candidatesCount(filters: $filters)
  }
`;

const CandidateSearch = () => {
  const user = useAuthStore((state) => state.user);
  const userLevel = user?.level || 0;
  const [filters, setFilters] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    const saved = localStorage.getItem('itemsPerPage');
    return saved ? parseInt(saved) : 25;
  });
  const [showFilters, setShowFilters] = useState(true);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [limitMessage, setLimitMessage] = useState('');
  
  // Turnstile is disabled if no site key configured
  const turnstileEnabled = !!process.env.REACT_APP_TURNSTILE_SITE_KEY;
  const [turnstileVerified, setTurnstileVerified] = useState(!turnstileEnabled);

  // Check if items per page is allowed for user level
  const isLimitAllowed = (limit) => isResultsLimitAllowed(limit, userLevel);

  // Get limit message based on selected limit
  const getLimitMessage = (selectedLimit) => {
    const maxAllowed = getMaxResultsPerPage(userLevel);
    if (selectedLimit <= maxAllowed) return '';
    if (selectedLimit <= 50) return 'Available from level 50';
    if (selectedLimit <= 100) return 'Available from level 100';
    return '';
  };

  const handleTurnstileVerify = (token) => {
    sessionStorage.setItem('turnstile-token', token);
    setTurnstileVerified(true);
  };

  useEffect(() => {
    // Skip if Turnstile is disabled
    if (!turnstileEnabled) return;
    
    // Check if token exists
    const existingToken = sessionStorage.getItem('turnstile-token');
    if (existingToken) {
      setTurnstileVerified(true);
    }
  }, [turnstileEnabled]);

  const { loading, error, data, refetch } = useQuery(GET_CANDIDATES, {
    variables: {
      filters,
      limit: itemsPerPage,
      offset: (currentPage - 1) * itemsPerPage
    },
    skip: !turnstileVerified, // Don't run query until CAPTCHA verified
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  });

  if (error) {
    if (error.networkError?.statusCode === 403) {
      return (
        <div className="error">
          <p>Please verify you are human.</p>
        </div>
      );
    }
    return (
      <div className="error">
        <p>Can't load co-founders, retry later.</p>
      </div>
    );
  }

  if (!turnstileVerified) {
    return (
      <div className="candidate-search">
        <div className="captcha-loading">
          <h2>Verifying you're human...</h2>
          <p>Please wait while we verify your browser.</p>
          <TurnstileWidget onVerify={handleTurnstileVerify} />
        </div>
      </div>
    );
  }

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
    // Don't automatically hide filters - let user control visibility
    refetch({
      filters: newFilters,
      limit: itemsPerPage,
      offset: 0
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    refetch({
      filters,
      limit: itemsPerPage,
      offset: (page - 1) * itemsPerPage
    });
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    if (!isLimitAllowed(newItemsPerPage)) {
      setLimitMessage(getLimitMessage(newItemsPerPage));
      setTimeout(() => setLimitMessage(''), 3000);
      return;
    }
    setItemsPerPage(newItemsPerPage);
    localStorage.setItem('itemsPerPage', newItemsPerPage.toString());
    setCurrentPage(1);
    refetch({
      filters,
      limit: newItemsPerPage,
      offset: 0
    });
  };

  const handleCandidateClick = (candidate) => {
    setSelectedCandidate(candidate);
  };

  const handleCloseModal = () => {
    setSelectedCandidate(null);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleRefresh = () => {
    refetch();
  };

  const totalItems = data?.candidatesCount ?? 0;
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / itemsPerPage) : 0;
  const candidates = data?.candidates || [];
  const rangeStart = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const rangeEnd = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="candidate-search">
      <div className={`search-container ${showFilters ? 'filters-visible' : 'filters-hidden'}`}>
        <div className={`filter-sidebar ${showFilters ? 'visible' : 'hidden'}`}>
          <div className="filter-container">
            <FilterPanel
              onFilterChange={handleFilterChange}
              onToggleFilters={toggleFilters}
              showFilters={showFilters}
            />
          </div>
        </div>

        {!showFilters && (
          <button className="show-filters-btn" onClick={toggleFilters} title="Show Filters">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 17L11 12L6 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M13 17L18 12L13 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}

        <div className="results-section">
          {error ? (
            <div className="error-state">
              <p>Something went wrong while loading candidates.</p>
              <button className="refresh-btn" onClick={handleRefresh}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 4V10H7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M23 20V14H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M3.51 15A9 9 0 0 0 18.36 18.36L23 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Refresh
              </button>
            </div>
          ) : loading ? (
            <div className="loading">Loading co-founders...</div>
          ) : (
            <CandidateTable
              candidates={candidates}
              onCandidateClick={handleCandidateClick}
            />
          )}
        </div>
      </div>

      {/* Footer Pagination */}
      <footer className="pagination-footer">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChange}
          loading={loading}
          isAdmin={false}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
        />
        {limitMessage && (
          <div className="limit-message">{limitMessage}</div>
        )}
      </footer>

      {selectedCandidate && (
        <CandidateModal
          candidate={selectedCandidate}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default CandidateSearch;